import React from "react";
import { Text, type GetProps, type SizeTokens } from "tamagui";
import {
  ThemeableStack,
  createStyledContext,
  styled,
  withStaticProperties,
} from "tamagui";

type AlignCells = {
  y: "center" | "start" | "end";
  x: "center" | "start" | "end";
};

type AlignHeaderCells = AlignCells;

export const TableContext = createStyledContext<{
  cellWidth: SizeTokens | number;
  cellHeight: SizeTokens | number;
  alignHeaderCells: {
    y: "center" | "start" | "end";
    x: "center" | "start" | "end";
  };
  alignCells: {
    y: "center" | "start" | "end";
    x: "center" | "start" | "end";
  };
  borderColor: string;
}>({
  cellWidth: "$20",
  cellHeight: "$7",
  alignHeaderCells: { x: "start", y: "center" },
  alignCells: { x: "start", y: "center" },
  borderColor: "$borderColor",
});

/** Table Components */
const Row = styled(ThemeableStack, {
  tag: "tr",
  flexDirection: "row",
  context: TableContext,
  borderColor: "$white4",
  variants: {
    rowLocation: {
      first: () => ({
        borderBottomWidth: 1,
      }),
      last: () => ({
        borderBottomWidth: 0,
      }),
      middle: () => ({
        borderBottomWidth: 1,
      }),
    },
  },
});

const StyledCellStack = styled(ThemeableStack, {
  tag: "td",
  flexDirection: "column",
  context: TableContext,
  flexGrow: 0,
  flexShrink: 1,
  paddingHorizontal: "$3.5",
  paddingVertical: "$2",
  variants: {
    cellWidth: {
      "...size": (name, { tokens }) => ({
        width: tokens.size[name],
      }),
    },
    cellHeight: {
      "...size": (name, { tokens }) => ({
        minHeight: tokens.size[name],
      }),
    },
    alignCells: (val: AlignCells) => ({
      alignItems: val.x === "center" ? "center" : `flex-${val.x}`,
      justifyContent: val.y === "center" ? "center" : `flex-${val.y}`,
    }),
  } as const,
});

const Cell = ({ children, ...props }: GetProps<typeof StyledCellStack>) => (
  <StyledCellStack {...props}>
    {typeof children === "string" ? (
      <Text textAlign="left">{children}</Text>
    ) : (
      children
    )}
  </StyledCellStack>
);

const StyledHeaderCellStack = styled(ThemeableStack, {
  tag: "th",
  flexDirection: "column",
  context: TableContext,
  flexGrow: 0,
  flexShrink: 1,
  padding: "$3.5",
  variants: {
    cellWidth: {
      "...size": (name, { tokens }) => ({
        width: tokens.size[name],
      }),
    },
    alignHeaderCells: (val: AlignHeaderCells) => ({
      alignItems: val.x === "center" ? "center" : `flex-${val.x}`,
      justifyContent: val.y === "center" ? "center" : `flex-${val.y}`,
    }),
  } as const,
});

const HeaderCell = ({
  children,
  ...props
}: GetProps<typeof StyledHeaderCellStack>) => (
  <StyledHeaderCellStack {...props}>
    {typeof children === "string" ? (
      <Text textAlign="left">{children}</Text>
    ) : (
      children
    )}
  </StyledHeaderCellStack>
);

const TableBody = styled(ThemeableStack, {
  tag: "tbody",
  flexDirection: "column",
  context: TableContext,
  flexShrink: 1,
  backgroundColor: "$gray2",
  borderBottomLeftRadius: "$6",
  borderBottomRightRadius: "$6",
});

const TableHead = styled(ThemeableStack, {
  tag: "thead",
  flexDirection: "column",
  context: TableContext,
  flexShrink: 1,
  backgroundColor: "$gray4",
  borderTopLeftRadius: "$6",
  borderTopRightRadius: "$6",
});

const TableFoot = styled(ThemeableStack, {
  tag: "tfoot",
  flexDirection: "column",
  context: TableContext,
  flexShrink: 1,
});

const TableComp = styled(ThemeableStack, {
  tag: "table",
  context: TableContext,
  variants: {
    /** just added these empty variants to avoid ts erros on Table */
    cellWidth: {
      "...size": () => ({}),
    },
    cellHeight: {
      "...size": () => ({}),
    },
    alignHeaderCells: (_val) => ({}),
    alignCells: (_val) => ({}),
  },
});

export const Table = withStaticProperties(TableComp, {
  Head: TableHead,
  Body: TableBody,
  Row,
  Cell,
  HeaderCell,
  Foot: TableFoot,
});
